import type {
  AttachmentType,
  PlatformChatKnowledgeBase,
  PlatformChatParticipant,
  PlatformChatSession,
  PlatformMessage,
  PlatformMessageAttachment,
  PlatformTurn,
} from '@liks/backend-svc-api/esm/models/index';
import { backendApiConfiguration } from './backendApi';
import { PlatformChatApi } from '@liks/backend-svc-api/esm/apis/PlatformChatApi';
import type { PlatformChatSessionListItemWithIdentity } from '../types/chatSession';
import * as platformChatMessageOps from './platformChatMessageOps';
import * as platformChatSessionOps from './platformChatSessionOps';
import { streamPlatformAssistantReply } from './streamPlatformAssistantReply';

type PlatformChatSendOptions =
  platformChatMessageOps.SendPlatformMessageOptions;
type PlatformChatRegenerateOptions =
  platformChatMessageOps.RegeneratePlatformReplyOptions;

class PlatformChatService {
  private readonly platformChatApi = new PlatformChatApi(
    backendApiConfiguration,
  );

  createSession(title: string): Promise<PlatformChatSession & { id: string }> {
    return platformChatSessionOps.createSession(this.platformChatApi, title);
  }

  listPinnedSessions(options: {
    limit: number;
    offset: number;
    search?: string;
  }): Promise<PlatformChatSessionListItemWithIdentity[]> {
    return platformChatSessionOps.listPinnedSessions(
      this.platformChatApi,
      options,
    );
  }

  listUnpinnedSessions(options: {
    limit: number;
    offset: number;
    search?: string;
  }): Promise<PlatformChatSessionListItemWithIdentity[]> {
    return platformChatSessionOps.listUnpinnedSessions(
      this.platformChatApi,
      options,
    );
  }

  getSession(
    sessionId: string,
  ): Promise<(PlatformChatSession & { id: string }) | undefined> {
    return platformChatSessionOps.getSession(this.platformChatApi, sessionId);
  }

  renameSession(session: PlatformChatSession, title: string): Promise<void> {
    return platformChatSessionOps.renameSession(
      this.platformChatApi,
      session,
      title,
    );
  }

  deleteSession(sessionId: string): Promise<void> {
    return platformChatSessionOps.deleteSession(
      this.platformChatApi,
      sessionId,
    );
  }

  pinSession(sessionId: string): Promise<void> {
    return platformChatSessionOps.pinSession(this.platformChatApi, sessionId);
  }

  unpinSession(sessionId: string): Promise<void> {
    return platformChatSessionOps.unpinSession(this.platformChatApi, sessionId);
  }

  setSessionSystemPrompt(
    sessionId: string,
    systemPrompt: string,
  ): Promise<void> {
    return platformChatSessionOps.setSessionSystemPrompt(
      this.platformChatApi,
      sessionId,
      systemPrompt,
    );
  }

  listParticipants(
    sessionId: string,
  ): Promise<Array<PlatformChatParticipant & { id: string; userId: string }>> {
    return platformChatSessionOps.listParticipants(
      this.platformChatApi,
      sessionId,
    );
  }

  inviteParticipant(sessionId: string, userId: string): Promise<void> {
    return platformChatSessionOps.inviteParticipant(
      this.platformChatApi,
      sessionId,
      userId,
    );
  }

  removeParticipant(sessionId: string, participantId: string): Promise<void> {
    return platformChatSessionOps.removeParticipant(
      this.platformChatApi,
      sessionId,
      participantId,
    );
  }

  transferOwnership(sessionId: string, newOwnerUserId: string): Promise<void> {
    return platformChatSessionOps.transferOwnership(
      this.platformChatApi,
      sessionId,
      newOwnerUserId,
    );
  }

  linkKnowledgeBase(sessionId: string, knowledgeBaseId: string): Promise<void> {
    return platformChatSessionOps.linkKnowledgeBase(
      this.platformChatApi,
      sessionId,
      knowledgeBaseId,
    );
  }

  listLinkedKnowledgeBases(
    sessionId: string,
  ): Promise<
    Array<PlatformChatKnowledgeBase & { id: string; knowledgeBaseId: string }>
  > {
    return platformChatSessionOps.listLinkedKnowledgeBases(
      this.platformChatApi,
      sessionId,
    );
  }

  unlinkKnowledgeBase(sessionId: string, linkId: string): Promise<void> {
    return platformChatSessionOps.unlinkKnowledgeBase(
      this.platformChatApi,
      sessionId,
      linkId,
    );
  }

  listMessages(
    sessionId: string,
    options?: { limit?: number; offset?: number },
  ): Promise<Array<PlatformMessage & { id: string }>> {
    return platformChatMessageOps.listMessages(
      this.platformChatApi,
      sessionId,
      options,
    );
  }

  sendMessage(
    sessionId: string,
    content: string,
    options?: PlatformChatSendOptions,
  ): Promise<PlatformTurn> {
    return platformChatMessageOps.sendMessage(
      this.platformChatApi,
      sessionId,
      content,
      options,
    );
  }

  regenerateReply(
    sessionId: string,
    messageId: string,
    options?: PlatformChatRegenerateOptions,
  ): Promise<(PlatformMessage & { id: string }) | undefined> {
    return platformChatMessageOps.regenerateReply(
      this.platformChatApi,
      sessionId,
      messageId,
      options,
    );
  }

  listMessageAttachments(
    sessionId: string,
    messageId: string,
  ): Promise<Array<PlatformMessageAttachment & { id: string }>> {
    return platformChatMessageOps.listMessageAttachments(
      this.platformChatApi,
      sessionId,
      messageId,
    );
  }

  downloadMessageAttachment(
    sessionId: string,
    messageId: string,
    attachmentId: string,
  ): Promise<Blob> {
    return platformChatMessageOps.downloadMessageAttachment(
      this.platformChatApi,
      sessionId,
      messageId,
      attachmentId,
    );
  }

  uploadSessionDraftAttachment(
    sessionId: string,
    file: File,
    attachmentType: AttachmentType = 'DOCUMENT',
  ): Promise<PlatformMessageAttachment & { id: string }> {
    return platformChatMessageOps.uploadSessionDraftAttachment(
      this.platformChatApi,
      sessionId,
      file,
      attachmentType,
    );
  }

  transcribeAudio(file: File): Promise<string> {
    return platformChatMessageOps.transcribeAudio(this.platformChatApi, file);
  }

  deleteSessionDraftAttachment(
    sessionId: string,
    attachmentId: string,
  ): Promise<void> {
    return platformChatMessageOps.deleteSessionDraftAttachment(
      this.platformChatApi,
      sessionId,
      attachmentId,
    );
  }

  listSessionDraftAttachments(
    sessionId: string,
    limit = 100,
  ): Promise<Array<PlatformMessageAttachment & { id: string }>> {
    return platformChatMessageOps.listSessionDraftAttachments(
      this.platformChatApi,
      sessionId,
      limit,
    );
  }

  streamAssistantReply(
    sessionId: string,
    content: string,
    handlers: {
      onChunk: (chunk: string) => void;
      onFirstChunk?: () => void;
    },
    options?: PlatformChatSendOptions,
  ): Promise<PlatformTurn> {
    return streamPlatformAssistantReply(
      this.platformChatApi,
      sessionId,
      content,
      handlers,
      options,
    );
  }
}

export const platformChatService = new PlatformChatService();
